import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, MessageCircle, Lock, Unlock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { api } from "../lib/api";
import { fdate, money, num, run, thisMonth, useLoad } from "../lib/util";
import { Alert, Badge, Btn, Field, Input, Select, Table, Empty } from "../components/kit";
import { waLink } from "../lib/whatsapp";

const METHOD = { pix: "Pix", boleto: "Boleto" };
export const dueFor = (ref) => {
  const last = new Date(Number(ref.slice(0, 4)), Number(ref.slice(5, 7)), 0).getDate();
  return `${ref}-${String(Math.min(30, last)).padStart(2, "0")}`;
};
export const isOverdue = (p) => ["pendente", "atrasado"].includes(p.status) && p.due_date < new Date().toLocaleDateString("sv-SE");

export async function toggleBlock(s, done) {
  const blocked = s.subscription_status === "suspensa";
  if (!window.confirm(blocked ? `Desbloquear ${s.name}? O gestor volta a acessar o MapIA.` : `Bloquear ${s.name}? O gestor ficará sem acesso até o desbloqueio.`)) return;
  const ok = await run(() => api.post(`/admin/schools/${s.id}/${blocked ? "unblock" : "block"}`, blocked ? undefined : { reason: "Mensalidade vencida" }), blocked ? "Unidade desbloqueada" : "Unidade bloqueada");
  if (ok) {
    window.dispatchEvent(new Event("mapia:admin"));
    done();
  }
}

export function chargeText(p, s) {
  const ref = `${p.reference.slice(5)}/${p.reference.slice(0, 4)}`;
  const lines = [
    `Olá${s.gestor_name ? `, ${s.gestor_name}` : ""}! Segue a cobrança da mensalidade do MapIA da escola ${s.name}.`,
    `Referência: ${ref}`,
    `Valor: ${money(p.amount)}`,
    `Vencimento: ${fdate(p.due_date)}`,
    `Forma de pagamento: ${METHOD[p.method] || p.method}`,
  ];
  if (p.payment_info) lines.push(`${p.method === "pix" ? "Chave Pix / código" : "Linha digitável do boleto"}: ${p.payment_info}`);
  if (p.status === "atrasado") lines.push("Consta em atraso. Se já pagou, por favor desconsidere e envie o comprovante.");
  lines.push("WebFast Soluções Inteligentes");
  return lines.join("\n");
}

const ST = [["pendente", "Pendente"], ["pago", "Pago"], ["atrasado", "Atrasado"], ["cancelado", "Cancelado"]];
const TONE = { pendente: "amber", pago: "green", atrasado: "red", cancelado: "muted" };

function PaymentDialog({ initial, schools, plans, onClose, onSaved }) {
  const [f, setF] = useState({ school_id: "", reference: thisMonth(), amount: "", due_date: dueFor(thisMonth()), status: "pendente", paid_at: "", method: "pix", payment_info: "", notes: "", ...initial });
  const set = (k) => (e) => {
    const v = e.target.value;
    const n = { ...f, [k]: v };
    if (k === "reference" && v) n.due_date = dueFor(v);
    if (k === "school_id" && !f.id) {
      const price = plans.find((p) => p.id === schools.find((s) => s.id === v)?.plan_id)?.price;
      if (price) n.amount = String(price).replace(".", ",");
    }
    setF(n);
  };
  const save = async () => {
    const miss = [[!f.school_id, "a escola"], [!(num(f.amount) > 0), "o valor"], [!f.due_date, "o vencimento"], [!f.reference, "o mês de referência"]].filter((x) => x[0]).map((x) => x[1]);
    if (miss.length) return run(() => Promise.reject(new Error(`Informe ${miss.join(", ")}`)));
    const body = { school_id: f.school_id, reference: f.reference, amount: num(f.amount), status: f.status, due_date: f.due_date || null, paid_at: f.paid_at || null, method: f.method, payment_info: f.payment_info || null, notes: f.notes || null };
    if (await run(() => (f.id ? api.put(`/admin/payments/${f.id}`, body) : api.post("/admin/payments", body)), "Pagamento salvo")) {
      window.dispatchEvent(new Event("mapia:admin"));
      onSaved();
      onClose();
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="payment-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Editar mensalidade" : "Registrar mensalidade"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Escola" className="col-span-2"><Select data-testid="payment-school-select" value={f.school_id} onChange={set("school_id")} placeholder="Selecione" options={schools.map((s) => [s.id, s.name])} /></Field>
          <Field label="Mês de referência"><Input data-testid="payment-reference-input" type="month" value={f.reference} onChange={set("reference")} /></Field>
          <Field label="Valor (R$)"><Input data-testid="payment-amount-input" value={f.amount} onChange={set("amount")} /></Field>
          <Field label="Vencimento (padrão: dia 30)"><Input data-testid="payment-due-input" type="date" value={f.due_date || ""} onChange={set("due_date")} /></Field>
          <Field label="Situação"><Select data-testid="payment-status-select" value={f.status} onChange={set("status")} options={ST} /></Field>
          <Field label="Pago em"><Input type="date" value={f.paid_at || ""} onChange={set("paid_at")} /></Field>
          <Field label="Forma de pagamento"><Select data-testid="payment-method-select" value={f.method} onChange={set("method")} options={Object.entries(METHOD)} /></Field>
          <Field label={f.method === "pix" ? "Chave Pix ou código copia e cola (opcional)" : "Linha digitável do boleto (opcional)"} className="col-span-2"><Input data-testid="payment-info-input" value={f.payment_info || ""} onChange={set("payment_info")} /></Field>
          <Field label="Observações" className="col-span-2"><Input value={f.notes || ""} onChange={set("notes")} /></Field>
        </div>
        <DialogFooter>
          <Btn variant="outline" onClick={onClose}>Cancelar</Btn>
          <Btn data-testid="payment-save-button" onClick={save}>Salvar</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContactSetting() {
  const [v, setV] = useState("");
  useEffect(() => {
    api.get("/public/contact").then((r) => setV(r.data.support_whatsapp || ""));
  }, []);
  const save = () => run(() => api.put("/admin/settings/contact", { support_whatsapp: v }), "WhatsApp do time MapIA salvo");
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="support-whatsapp-setting">
      <Field label="WhatsApp do time MapIA (aparece para a unidade bloqueada)" className="min-w-[260px]"><Input data-testid="support-whatsapp-input" placeholder="(21) 99999-9999" value={v} onChange={(e) => setV(e.target.value)} /></Field>
      <Btn variant="outline" data-testid="support-whatsapp-save" onClick={save}>Salvar</Btn>
    </div>
  );
}

export default function PaymentsTab() {
  const [pays, reload] = useLoad(async () => (await api.get("/admin/payments")).data, []);
  const [schools, reloadSchools] = useLoad(async () => (await api.get("/admin/schools")).data, []);
  const [plans] = useLoad(async () => (await api.get("/admin/plans")).data, []);
  const [edit, setEdit] = useState(null);
  const sname = (id) => (schools || []).find((s) => s.id === id)?.name || "—";
  const setStatus = async (p, status) => {
    const body = { ...p, status, paid_at: status === "pago" ? p.paid_at || new Date().toISOString().slice(0, 10) : p.paid_at };
    ["id", "created_at"].forEach((k) => delete body[k]);
    if (await run(() => api.put(`/admin/payments/${p.id}`, body), status === "pago" ? "Marcada como paga" : "Marcada em atraso")) {
      window.dispatchEvent(new Event("mapia:admin"));
      reload();
    }
  };
  const del = async (p) => {
    if (window.confirm("Remover este registro?") && (await run(() => api.delete(`/admin/payments/${p.id}`), "Removido"))) {
      window.dispatchEvent(new Event("mapia:admin"));
      reload();
    }
  };
  return (
    <div className="mt-4 space-y-4">
      <Alert tone="info" title="Cobrança manual (Pix ou boleto)">
        Nenhum meio de pagamento está integrado. Registre cada cobrança (vencimento todo dia 30), envie o boleto ou PIX pelo WhatsApp da escola e marque como paga ou em atraso. Depois do vencimento, use "Bloquear unidade" para suspender o acesso da escola que não pagou.
      </Alert>
      <div className="flex flex-wrap justify-between items-end gap-3"><ContactSetting /><Btn data-testid="new-payment-button" onClick={() => setEdit({})}><Plus className="w-4 h-4" />Registrar mensalidade</Btn></div>
      <Table testid="payments-table">
        <thead><tr><th>Escola</th><th>Referência</th><th className="num">Valor</th><th>Vencimento</th><th>Situação</th><th>Pago em</th><th>Forma</th><th /></tr></thead>
        <tbody>
          {(pays || []).map((p) => (
            <tr key={p.id}>
              <td className="font-semibold">{sname(p.school_id)}</td>
              <td>{p.reference.slice(5)}/{p.reference.slice(0, 4)}</td>
              <td className="num">{money(p.amount)}</td>
              <td>{fdate(p.due_date)}{isOverdue(p) && <div><Badge tone="red">Vencida</Badge></div>}</td>
              <td><Badge tone={TONE[p.status]}>{ST.find((x) => x[0] === p.status)[1]}</Badge></td>
              <td>{fdate(p.paid_at)}</td>
              <td>{METHOD[p.method] || p.method}</td>
              <td className="whitespace-nowrap">
                {(() => {
                  const s = (schools || []).find((x) => x.id === p.school_id);
                  const wa = s && p.status !== "pago" && p.status !== "cancelado" && waLink(s.whatsapp, chargeText(p, s));
                  if (p.status === "pago" || p.status === "cancelado") return null;
                  return wa ? <a href={wa} target="_blank" rel="noreferrer"><Btn data-testid={`payment-whatsapp-${p.id}`} className="px-2.5"><MessageCircle className="w-4 h-4" />Cobrar no WhatsApp</Btn></a>
                    : <Badge tone="amber" title="Cadastre o WhatsApp no cadastro da escola">Sem WhatsApp</Badge>;
                })()}
                {p.status !== "pago" && <Btn variant="outline" data-testid={`payment-paid-${p.id}`} onClick={() => setStatus(p, "pago")}>Pago</Btn>}
                {p.status !== "atrasado" && p.status !== "pago" && <Btn variant="ghost" data-testid={`payment-late-${p.id}`} onClick={() => setStatus(p, "atrasado")}>Em atraso</Btn>}
                {(() => {
                  const s = (schools || []).find((x) => x.id === p.school_id);
                  if (!s) return null;
                  if (s.subscription_status === "suspensa") return <Btn variant="outline" data-testid={`payment-unblock-${p.id}`} onClick={() => toggleBlock(s, reloadSchools)}><Unlock className="w-4 h-4" />Desbloquear</Btn>;
                  return isOverdue(p) ? <Btn variant="danger" data-testid={`payment-block-${p.id}`} onClick={() => toggleBlock(s, reloadSchools)}><Lock className="w-4 h-4" />Bloquear unidade</Btn> : null;
                })()}
                <Btn variant="ghost" onClick={() => setEdit(p)}><Pencil className="w-4 h-4" /></Btn>
                <Btn variant="ghost" onClick={() => del(p)}><Trash2 className="w-4 h-4 text-red-600" /></Btn>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {pays && !pays.length && <Empty>Nenhuma mensalidade registrada.</Empty>}
      {edit && <PaymentDialog initial={edit} schools={schools || []} plans={plans || []} onClose={() => setEdit(null)} onSaved={reload} />}
    </div>
  );
}
